import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Clock, CheckCircle, XCircle, Eye, EyeOff, Filter, ExternalLink,
  ChevronRight, Phone, MapPin, Receipt, Utensils, AlertCircle, Volume2, VolumeX, MessageCircle, Zap, Power
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import useSound from 'use-sound';

interface Pedido {
  id: number;
  mesa: string;
  status: string;
  total: number;
  observacoes?: string;
  created_at: string;
  total_itens?: number;
  tipo_pedido: string;
  valor_frete?: number;
  total_sem_frete?: number;
  nome_completo_cliente?: string;
  telefone_cliente?: string;
  email_cliente?: string;
  endereco_entrega?: string | any;
  metodo_pagamento?: string;
  metodo_pagamento_detalhado?: string;
  status_pagamento?: string;
  precisa_troco?: boolean;
  valor_troco?: string | number;
  itens?: PedidoItem[];
  [key: string]: any;
}

interface PedidoItem {
  id: number;
  produto_nome: string;
  quantidade: number;
  preco_unitario: number;
  observacoes: string;
  personalizacoes: Personalizacao[];
}

interface Personalizacao {
  id: number;
  nome: string;
  tipo: 'adicionar' | 'remover';
  quantidade: number;
  preco_adicional: number;
}

interface ChamadaGarcom {
  id: number;
  mesa: string;
  status: string;
  created_at: string;
  empresa_id: number;
}

const PedidosPage: React.FC = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [chamadasGarcom, setChamadasGarcom] = useState<ChamadaGarcom[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Abas e modais
  const [activeTab, setActiveTab] = useState<'operacional' | 'historico'>('operacional');
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  
  // Limpeza de Histórico
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  
  // Filtros Avançados & Automações (Carregando do Cache)
  const [filterTipo, setFilterTipo] = useState<'todos' | 'entrega' | 'mesa'>(() => {
    return (localStorage.getItem('pedidos_filterTipo') as any) || 'todos';
  });
  
  const [autoAccept, setAutoAccept] = useState(() => {
    return localStorage.getItem('pedidos_autoAccept') === 'true';
  });

  // Áudio
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('pedidos_soundEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  // Salvar preferências a cada clique
  useEffect(() => {
    localStorage.setItem('pedidos_filterTipo', filterTipo);
  }, [filterTipo]);

  useEffect(() => {
    localStorage.setItem('pedidos_autoAccept', autoAccept.toString());
  }, [autoAccept]);

  useEffect(() => {
    localStorage.setItem('pedidos_soundEnabled', soundEnabled.toString());
  }, [soundEnabled]);
  const knownPendingRef = useRef<Set<string>>(new Set());
  const isFirstLoadRef = useRef<boolean>(true);

  // Sistema Profissional de Áudio (Bypass Autoplay via use-sound)
  const [isSoundUnlocked, setIsSoundUnlocked] = useState(false);
  
  // A bilbioteca puxa o mp3 raiz do projeto e gerencia fila/cache
  const [playNotificationSoundLocal] = useSound('/assets/ding.mp3', { volume: 1.0 });

  const playNotificationSound = () => {
    if (!soundEnabled) return;
    try {
      playNotificationSoundLocal();
      // O toque Duplo (1.5s de respiro igual implementado antes)
      setTimeout(playNotificationSoundLocal, 1500); 
    } catch (e) {
      console.error('Erro na engine de áudio:', e);
    }
  };

  useEffect(() => {
    fetchPedidos();
    fetchChamadasGarcom();
    const interval = setInterval(() => {
      fetchPedidos();
      fetchChamadasGarcom();
    }, 15000); // 15s refetch
    return () => clearInterval(interval);
  }, [autoAccept]);

  const fetchPedidos = async () => {
    try {
      const response = await api.get('/api/pedidos');
      let data: Pedido[] = response.data || [];
      
      // Ocultar pedidos problemáticos ou não anexados (status "entregue" ou sem amarração) conforme regra.
      // O back-end pode já mandar esses caras finalizados. A gente limpa quem não for mais ser visto no operacional.
      
      if (data && data.length > 0) {
        // Tracker blindado por "Assinatura" do Pedido (!colisões de IDs vs Origem)
        const getOrderKey = (p: Pedido) => `${p.origem || p.tipo_pedido}-${p.id}`;
        
        const pendentesAtuais = data.filter(p => p.status === 'pendente');
        
        // Se a chave dele não tá na memória do navegador, é pedido virgem
        const novosPedPendentes = pendentesAtuais.filter(p => !knownPendingRef.current.has(getOrderKey(p)));

        // Se for 2ª renderização em diante E tiver virgem, APITA
        if (!isFirstLoadRef.current && novosPedPendentes.length > 0) {
          playNotificationSound();
          toast(`Novo Pedido Recebido! 🔥`, {
            icon: '🔔',
            style: { background: '#FEF3C7', color: '#92400E', fontWeight: 'bold', minWidth: '250px' }
          });

          // LÓGICA DE AUTO-ACEITE
          if (autoAccept) {
             novosPedPendentes.forEach(async (np) => {
               try {
                 await api.put(`/api/pedidos/${np.id}/status`, { status: 'preparando' });
                 toast.success(`Pedido #${np.id} ACEITO AUTOMATICAMENTE!`, { icon: '🤖' });
                 np.status = 'preparando'; 
               } catch (err) {
                 console.error('Falha ao auto-aceitar ped', np.id, err);
               }
             });
          }
        }
        
        // Atualiza a memória para não apitar esses mesmos no próximo segundo
        pendentesAtuais.forEach(p => knownPendingRef.current.add(getOrderKey(p)));
      }
      
      isFirstLoadRef.current = false;
      
      // Remove do fluxo do painel principal (estado Operacional) os dados finalizados de mesa
      // O usuário pediu "quando não tiver mais a comanda, é pq foi entregue... pode excluir"
      // Aqui, limpamos explicitamente o que for "entregue" do array local "operacional", mantendo-o apenas pelo .filter do histórico.
      setPedidos(data);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!adminPassword) {
      toast.error('Digite a senha para autorizar o expurgo.');
      return;
    }
    
    // Mostra um progresso
    toast.loading('Dando rollback em cascata no banco...', { id: 'clearing' });
    try {
      const resp = await api.delete('/api/pedidos/limpar-historico', { 
        data: { senhaAdmin: adminPassword } 
      });
      toast.success(resp.data.message || 'Histórico apagado do banco de dados.', { id: 'clearing' });
      setShowClearHistoryModal(false);
      setAdminPassword('');
      fetchPedidos(); // Atualizar tela
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Falha de permissão ao expurgar.', { id: 'clearing' });
    }
  };

  const handleClearDelivery = async () => {
    const deliveryProntos = prontos.filter(p => p.tipo_pedido === 'entrega' || p.tipo_pedido === 'retirada');
    if (deliveryProntos.length === 0) {
      toast.error('Não há pedidos de delivery/retirada prontos para limpar.');
      return;
    }

    if (!window.confirm(`Deseja marcar todos os ${deliveryProntos.length} pedidos de delivery como entregues?`)) {
      return;
    }

    toast.loading('Finalizando entregas...', { id: 'clearing-delivery' });
    try {
      const resp = await api.post('/api/pedidos/limpar-delivery');
      toast.success(resp.data.message || 'Pedidos finalizados!', { id: 'clearing-delivery' });
      fetchPedidos();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Falha ao limpar delivery.', { id: 'clearing-delivery' });
    }
  };

  const fetchChamadasGarcom = async () => {
    try {
      const response = await api.get('/api/chamar-garcom/1');
      setChamadasGarcom(response.data);
    } catch (error) {
      console.error('Erro ao carregar chamadas de garçom:', error);
    }
  };

  const updateStatus = async (id: number, status: string, pedido: Pedido) => {
    try {
      await api.put(`/api/pedidos/${id}/status`, { status });
      toast.success(`Pedido movido para: ${status.toUpperCase()}`);
      setSelectedPedido(null);
      fetchPedidos();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const updateStatusChamada = async (id: number, status: string) => {
    try {
      await api.put(`/api/chamar-garcom/${id}/status`, { status });
      toast.success('Chamada atualizada!');
      fetchChamadasGarcom();
    } catch (error) {
      toast.error('Erro ao atualizar chamada');
    }
  };

  // Funções Utilitárias
  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')} - ${d.getDate().toString().padStart(2, '0')}/${(d.getMonth()+1).toString().padStart(2, '0')}`;
  };

  const getTempoDecorrido = (dateString: string) => {
    const diffMs = new Date().getTime() - new Date(dateString).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins;
  };

  const enviarWhatsApp = (pedido: Pedido) => {
    if (!pedido.telefone_cliente) {
      toast.error('Pedido sem telefone');
      return;
    }
    const telefone = pedido.telefone_cliente.replace(/\D/g, '');
    let mensagem = `*PEDIDO #${pedido.id}*\n\n`;
    mensagem += `*Status:* ${pedido.status.toUpperCase()}\n*Total:* R$ ${Number(pedido.total).toFixed(2)}\n\n`;
    const url = `https://wa.me/55${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  };

  // Divisão Kanban e Filtros Top Level
  // Aplica Exclusão base do Kanban: 'entregue' e 'cancelado' vazam da visão Operacional.
  // Alem disso, filtra por "Delivery vs Mesa"
  const filtroVisaoLocal = (p: Pedido) => {
     if (filterTipo === 'todos') return true;
     return p.tipo_pedido === filterTipo;
  };

  const pendentes = pedidos.filter(p => p.status === 'pendente' && filtroVisaoLocal(p));
  const emPreparo = pedidos.filter(p => p.status === 'preparando' && filtroVisaoLocal(p));
  const prontos = pedidos.filter(p => p.status === 'pronto' && filtroVisaoLocal(p));
  
  // No histórico, a gente pode deixar o filtro de Delivery/Mesa ativado ou ignorar. 
  // Vou manter o filtro ativo também para que a aba respeite a seleção.
  const historico = pedidos.filter(p => ['entregue', 'cancelado'].includes(p.status) && filtroVisaoLocal(p));

  // Render do Card Minimalista para Colunas
  const renderCard = (pedido: Pedido, columnColorClass: string) => {
    const mins = getTempoDecorrido(pedido.created_at);
    let timeColor = "text-gray-500";
    if (mins > 30) timeColor = "text-red-500 font-bold";
    else if (mins > 15) timeColor = "text-orange-500 font-medium";

    const isDelivery = pedido.tipo_pedido === 'entrega';

    return (
      <div 
        key={pedido.id}
        onClick={() => setSelectedPedido(pedido)}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 cursor-pointer hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500 transition-all flex flex-col gap-2 relative group animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Barra de cor */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${columnColorClass}`} />
        
        <div className="flex justify-between items-start pl-2">
          <div>
            <span className="text-xs font-bold text-gray-400 dark:text-gray-500">#{pedido.id}</span>
            <h3 className="text-sm font-bold text-gray-800 dark:text-white mt-0.5 flex items-center gap-1.5">
              {isDelivery ? <><Utensils className="w-3.5 h-3.5 text-orange-500"/> Delivery</> : <><Utensils className="w-3.5 h-3.5 text-blue-500"/> Mesa {pedido.mesa}</>}
            </h3>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-sm font-bold text-gray-900 dark:text-white">R$ {Number(pedido.total).toFixed(2)}</span>
            <span className={`text-[10px] sm:text-xs flex items-center gap-1 mt-1 ${timeColor}`}>
              <Clock className="w-3 h-3" /> {mins} min
            </span>
          </div>
        </div>

        {isDelivery && pedido.nome_completo_cliente && (
          <div className="pl-2 mt-1">
             <div className="text-xs text-gray-600 dark:text-gray-300 truncate font-medium">
               {pedido.nome_completo_cliente}
             </div>
          </div>
        )}

        <div className="pl-2 mt-2 flex items-center justify-between">
          <span className="text-[10px] px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full font-medium">
            {pedido.itens ? pedido.itens.length : (pedido.total_itens || 0)} Itens
          </span>
          {pedido.status_pagamento === 'pago' ? (
             <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full font-medium flex items-center gap-1">
               <CheckCircle className="w-3 h-3"/> Pago
             </span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full font-medium">
               Aguard. Pgto
             </span>
          )}
        </div>
      </div>
    );
  };

  // Render Detalhes Modal
  const renderPedidoModal = () => {
    if (!selectedPedido) return null;
    const p = selectedPedido;
    const isDelivery = p.tipo_pedido === 'entrega';
    
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-md animate-in fade-in duration-200" onClick={() => setSelectedPedido(null)}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden ring-1 ring-gray-900/10" onClick={e => e.stopPropagation()}>
          
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  Pedido #{p.id}
                </h2>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full uppercase tracking-wider
                  ${p.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' : 
                    p.status === 'preparando' ? 'bg-blue-100 text-blue-800' : 
                    'bg-green-100 text-green-800'}`}>
                  {p.status}
                </span>
                {isDelivery && <span className="px-2 py-0.5 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 rounded-full text-xs font-bold">Entregar</span>}
              </div>
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Entrou às {formatDate(p.created_at)}
              </p>
            </div>
            <button onClick={() => setSelectedPedido(null)} className="p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-full transition-colors">
              <XCircle className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-900/30">
                <h3 className="text-sm font-bold text-blue-900 dark:text-blue-300 mb-3 flex items-center gap-2">
                  {isDelivery ? <MapPin className="w-4 h-4"/> : <Utensils className="w-4 h-4" />}
                  {isDelivery ? "Dados de Entrega" : "Local"}
                </h3>
                
                {isDelivery ? (
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <p><strong>Cliente:</strong> {p.nome_completo_cliente || 'N/A'}</p>
                    <p className="flex items-center gap-2">
                      <strong>Telefone:</strong> {p.telefone_cliente || 'N/A'}
                      {p.telefone_cliente && (
                        <button onClick={() => enviarWhatsApp(p)} className="text-green-600 hover:scale-110 transition-transform">
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      )}
                    </p>
                    <div className="pt-2 mt-2 border-t border-blue-200 dark:border-blue-800/50">
                      <strong>Endereço:</strong><br/>
                      <span className="text-xs">
                         {(() => {
                            try {
                              let end = typeof p.endereco_entrega === 'string' ? JSON.parse(p.endereco_entrega) : p.endereco_entrega;
                              if (end) return `${end.rua}, ${end.numero_casa} - ${end.bairro} (CEP: ${end.cep})`;
                            } catch { return String(p.endereco_entrega || 'N/A') }
                         })()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                    Mesa {p.mesa}
                  </div>
                )}
              </div>

              <div className="bg-purple-50/50 dark:bg-purple-900/10 rounded-xl p-4 border border-purple-100 dark:border-purple-900/30">
                <h3 className="text-sm font-bold text-purple-900 dark:text-purple-300 mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4"/> Resumo Financeiro
                </h3>
                <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>R$ {Number(p.total_sem_frete || (p.total - (p.valor_frete || 0))).toFixed(2)}</span>
                  </div>
                  {isDelivery && (
                    <div className="flex justify-between">
                      <span>Frete:</span>
                      <span>R$ {Number(p.valor_frete || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-purple-200 dark:border-purple-800/50 mt-2">
                    <span>Total:</span>
                    <span className="text-gray-900 dark:text-white">R$ {Number(p.total).toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-3 bg-white dark:bg-gray-800 rounded p-2 text-xs">
                  <p><strong>Método:</strong> {p.metodo_pagamento_detalhado || p.metodo_pagamento || 'N/A'}</p>
                  <p><strong>Status Pag:</strong> {p.status_pagamento === 'pago' ? '✅ PAGO' : '⏳ PENDENTE'}</p>
                  {p.precisa_troco && p.valor_troco && (
                    <p className="text-orange-600 dark:text-orange-400 font-bold mt-1">
                      ⚠️ Levar troco para: R$ {Number(p.valor_troco).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

            </div>

            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">📋 Itens Solicitados ({p.itens?.length || 0})</h3>
              
              {!p.itens || p.itens.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-500">Nenhum item carregado.</div>
              ) : (
                <div className="space-y-3">
                  {p.itens.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex justify-between font-bold text-gray-800 dark:text-gray-200 text-sm">
                        <span>{item.quantidade}x {item.produto_nome}</span>
                        <span>R$ {(item.preco_unitario * item.quantidade).toFixed(2)}</span>
                      </div>
                      
                      {Array.isArray(item.personalizacoes) && item.personalizacoes.length > 0 && (
                        <div className="mt-2 text-xs space-y-1 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                          {item.personalizacoes.filter((pl: any) => pl.tipo === 'adicionar').map((pl: any, i: number) => (
                            <div key={`add-${i}`} className="flex justify-between text-green-700 dark:text-green-400">
                              <span>+ {pl.quantidade || 1}x {pl.nome}</span>
                              <span>+ R$ {(pl.preco_adicional * (pl.quantidade || 1)).toFixed(2)}</span>
                            </div>
                          ))}
                          {item.personalizacoes.filter((pl: any) => pl.tipo === 'remover').map((pl: any, i: number) => (
                            <div key={`rem-${i}`} className="text-red-600 dark:text-red-400">
                              - Remover {pl.nome}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {item.observacoes && (
                        <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10 p-2 rounded flex gap-1">
                           <AlertCircle className="w-4 h-4" /> Obs: {item.observacoes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {p.observacoes && (
               <div className="bg-yellow-50 dark:bg-yellow-900/10 p-3 rounded-xl border border-yellow-200 dark:border-yellow-700/50">
                  <strong className="text-sm text-yellow-800 dark:text-yellow-400 block mb-1">Observações Gerais (Cozinha):</strong>
                  <p className="text-sm text-yellow-900 dark:text-yellow-300">{p.observacoes}</p>
               </div>
            )}
            
          </div>

          <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
            <div className="flex gap-3">
              {p.status === 'pendente' && (
                <>
                  <button onClick={() => updateStatus(p.id, 'cancelado', p)} className="flex-1 px-4 py-3 bg-white dark:bg-gray-700 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 rounded-xl font-bold hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                    Cancelar / Recusar
                  </button>
                  <button onClick={() => updateStatus(p.id, 'preparando', p)} className="flex-[2] px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 rounded-xl font-bold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]">
                    Imprimir & Mandar p/ Cozinha <ChevronRight className="w-5 h-5"/>
                  </button>
                </>
              )}
              {p.status === 'preparando' && (
                <button onClick={() => updateStatus(p.id, 'pronto', p)} className="w-full px-4 py-3 bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/20 rounded-xl font-bold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]">
                  Marcar como PRONTO <CheckCircle className="w-5 h-5"/>
                </button>
              )}
              {p.status === 'pronto' && (
                <button onClick={() => updateStatus(p.id, 'entregue', p)} className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20 rounded-xl font-bold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]">
                  CONCLUIR ENTREGA <CheckCircle className="w-5 h-5"/>
                </button>
              )}
               {['entregue', 'cancelado'].includes(p.status) && (
                <button onClick={() => setSelectedPedido(null)} className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-300 transition-colors">
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="text-gray-500 font-medium animate-pulse">Estabelecendo conexão com o salão...</p>
      </div>
    );
  }

  // OVERLAY OBRIGATÓRIO (BYPASS DO AUTOPLAY DO GOOGLE CHROME)
  if (!isSoundUnlocked) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] mt-[70px] bg-slate-900 overflow-hidden relative rounded-xl shadow-inner">
        <div className="absolute inset-0 bg-blue-500/10 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.15)_0,transparent_50%)]"></div>
        
        <div className="z-10 text-center space-y-8 max-w-lg p-6">
          <div className="inline-flex p-8 rounded-full bg-white/5 backdrop-blur-md shadow-2xl border border-white/10 relative">
            <div className="absolute inset-0 rounded-full border-2 border-blue-500/30 animate-ping"></div>
            <Volume2 className="w-20 h-20 text-blue-400" />
          </div>
          
          <div className="space-y-4">
            <h1 className="text-4xl font-black text-white tracking-tight">Painel de Recepção</h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Para garantir que as sirenes de Delivery toquem alto sem que o navegador silencie, inicie a sua sessão manualmente.
            </p>
          </div>
          
          <button 
             onClick={() => {
              playNotificationSoundLocal({ id: 'dummy', volume: 0.001 } as any); // Toca inaudível pra assinar o contrato com o Chrome
              setIsSoundUnlocked(true);
            }}
            className="group relative inline-flex items-center justify-center gap-3 px-10 py-5 bg-blue-600 font-bold text-white text-xl rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(37,99,235,0.3)] hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(37,99,235,0.5)] transition-all duration-300 ring-1 ring-white/10"
          >
            <Power className="w-6 h-6 group-hover:rotate-90 transition-transform duration-500" />
            <span>INICIAR TELA DE PEDIDOS</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 md:px-4 lg:px-6 h-[calc(100vh-80px)] overflow-hidden flex flex-col pt-4">
      
      {/* HEADER SUPERIOR E FILTROS */}
      <div className="flex flex-col xl:flex-row xl:justify-between items-start xl:items-center gap-4 mb-4 flex-shrink-0">
        
        {/* Título e Botões Globais */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight mr-2">
            Balcão de Pedidos
          </h1>

          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1.5 rounded-lg border transition-colors ${soundEnabled ? 'bg-orange-100 border-orange-200 text-orange-700 dark:bg-orange-900/30 dark:border-orange-800 dark:text-orange-400' : 'bg-gray-100 border-gray-200 text-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'}`}
            title={soundEnabled ? "Campainha Ativa (iFood)" : "Campainha Silenciada"}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>

          <button 
            onClick={() => {
              setAutoAccept(!autoAccept);
              if (!autoAccept) toast.success("Piloto Automático Ativado! 🚀");
            }}
            className={`px-3 py-1.5 rounded-lg border text-sm font-bold flex items-center gap-2 transition-all ${autoAccept ? 'bg-indigo-600 text-white border-indigo-700 shadow-md shadow-indigo-600/20 animate-pulse' : 'bg-white dark:bg-gray-800 text-gray-600 border-gray-200 dark:border-gray-700'}`}
            title="Aceitar Pedidos 'Pendentes' Automaticamente"
          >
            <Zap className={`w-4 h-4 ${autoAccept ? 'fill-white' : ''}`} /> 
            {autoAccept ? 'Auto-Aceite LIGADO' : 'Auto-Aceite Desl.'}
          </button>
        </div>
        
        {/* Filtros de Aba e Segmentação */}
        <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
          {/* Pills de Filtragem Tipo (Todos/Mesa/Delivery) */}
          <div className="bg-gray-100 dark:bg-gray-800/80 p-1 rounded-xl flex shadow-inner">
            <button onClick={() => setFilterTipo('todos')} className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all ${filterTipo === 'todos' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500'}`}>
              Tudo
            </button>
            <button onClick={() => setFilterTipo('entrega')} className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all flex items-center gap-1 ${filterTipo === 'entrega' ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 shadow-sm' : 'text-gray-500'}`}>
              🚚 Delivery
            </button>
            <button onClick={() => setFilterTipo('mesa')} className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all flex items-center gap-1 ${filterTipo === 'mesa' ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-500'}`}>
              🍽️ Mesa
            </button>
          </div>

          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 hidden sm:block mx-1"></div>

          {/* Abas Op/Histórico */}
          <div className="bg-gray-100 dark:bg-gray-800/80 p-1 rounded-xl flex shadow-inner">
            <button 
              onClick={() => setActiveTab('operacional')}
              className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all ${activeTab === 'operacional' ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-white shadow-sm' : 'text-gray-500'}`}
            >
              Operativo
            </button>
            <button 
              onClick={() => setActiveTab('historico')}
              className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all ${activeTab === 'historico' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500'}`}
            >
              Histórico
            </button>
          </div>
        </div>

      </div>

      {chamadasGarcom.length > 0 && activeTab === 'operacional' && (
        <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-xl p-3 flex-shrink-0 animate-pulse">
           <div className="flex items-center gap-2 mb-2">
             <span className="animate-ping h-2.5 w-2.5 rounded-full bg-red-500"></span>
             <h3 className="font-bold text-yellow-800 dark:text-yellow-400 text-sm">Chamados das Mesas em Espera</h3>
           </div>
           <div className="flex gap-2 overflow-x-auto pb-1">
             {chamadasGarcom.map(chamada => (
                <div key={chamada.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2 flex items-center gap-4 flex-shrink-0 border border-yellow-100 dark:border-yellow-800">
                   <div>
                     <p className="font-bold text-gray-800 dark:text-gray-200">Mesa {chamada.mesa}</p>
                     <p className="text-xs text-gray-500">{formatDate(chamada.created_at)}</p>
                   </div>
                   <div className="flex gap-1">
                     <button onClick={() => updateStatusChamada(chamada.id, 'atendida')} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 p-1.5 rounded hover:bg-green-200 transition-colors">
                       <CheckCircle className="w-4 h-4"/>
                     </button>
                     <button onClick={() => updateStatusChamada(chamada.id, 'cancelada')} className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 p-1.5 rounded hover:bg-red-200 transition-colors">
                       <XCircle className="w-4 h-4"/>
                     </button>
                   </div>
                </div>
             ))}
           </div>
        </div>
      )}

      {/* ÁREA DE BOARD */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'operacional' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 h-full pb-4">
            
            <div className="bg-gray-50/80 dark:bg-gray-900/40 rounded-2xl p-3 md:p-4 flex flex-col h-full overflow-hidden border border-gray-100 dark:border-gray-800 ring-1 ring-black/5 dark:ring-white/5">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2 tracking-wide">
                   <div className="w-2.5 h-2.5 rounded-full bg-yellow-400 animate-pulse"></div> ENTRARAM AGORA
                </h2>
                <span className="bg-white dark:bg-gray-800 shadow-sm text-gray-700 dark:text-gray-300 font-bold px-2.5 py-0.5 rounded-full text-xs">
                  {pendentes.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1 pb-10">
                {pendentes.length === 0 ? (
                  <div className="h-24 flex items-center justify-center text-sm font-medium text-gray-400 border border-dashed border-gray-200 dark:border-gray-700/50 rounded-xl">Zero pedidos novos</div>
                ) : (
                  pendentes.map(p => renderCard(p, "bg-yellow-400"))
                )}
              </div>
            </div>

            <div className="bg-indigo-50/50 dark:bg-indigo-900/10 rounded-2xl p-3 md:p-4 flex flex-col h-full overflow-hidden border border-indigo-100/50 dark:border-indigo-900/30">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-sm font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-2 tracking-wide">
                   <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div> NA COZINHA E CHAPA
                </h2>
                <span className="bg-white dark:bg-gray-800 shadow-sm text-indigo-700 dark:text-indigo-300 font-bold px-2.5 py-0.5 rounded-full text-xs">
                  {emPreparo.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1 pb-10">
                {emPreparo.length === 0 ? (
                  <div className="h-24 flex items-center justify-center text-sm font-medium text-indigo-300 dark:text-indigo-700/50 border border-dashed border-indigo-200 dark:border-indigo-800/50 rounded-xl">Cozinha ociosa</div>
                ) : (
                  emPreparo.map(p => renderCard(p, "bg-blue-500"))
                )}
              </div>
            </div>

            <div className="bg-emerald-50/50 dark:bg-emerald-900/10 rounded-2xl p-3 md:p-4 flex flex-col h-full overflow-hidden border border-emerald-100/50 dark:border-emerald-900/30">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-2 tracking-wide">
                   <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div> PRONTO / EXPEDIÇÃO
                </h2>
                <div className="flex items-center gap-2">
                  {prontos.some(p => p.tipo_pedido === 'entrega' || p.tipo_pedido === 'retirada') && (
                    <button 
                      onClick={handleClearDelivery}
                      className="text-[10px] font-bold bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700 transition-colors flex items-center gap-1"
                      title="Finalizar todas as entregas prontas"
                    >
                      <CheckCircle className="w-3 h-3" /> Limpar Delivery
                    </button>
                  )}
                  <span className="bg-white dark:bg-gray-800 shadow-sm text-emerald-700 dark:text-emerald-300 font-bold px-2.5 py-0.5 rounded-full text-xs">
                    {prontos.length}
                  </span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1 pb-10">
                {prontos.length === 0 ? (
                  <div className="h-24 flex items-center justify-center text-sm font-medium text-emerald-300 dark:text-emerald-700/50 border border-dashed border-emerald-200 dark:border-emerald-800/50 rounded-xl">Bancada limpa</div>
                ) : (
                  prontos.map(p => renderCard(p, "bg-green-500"))
                )}
              </div>
            </div>

          </div>
        ) : (
          <div className="h-full overflow-y-auto pb-20 custom-scrollbar">
             <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 border-b border-gray-100 dark:border-gray-700 pb-2 flex justify-between items-center">
                  Histórico de Fechamentos (Mesa e Balcão)
                  <button onClick={() => setShowClearHistoryModal(true)} className="text-xs md:text-sm font-bold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 hover:text-red-700 transition-colors flex items-center gap-1">
                     <AlertCircle className="w-4 h-4"/> Limpar Histórico
                  </button>
                </h2>
                {historico.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 opacity-50">
                     <Utensils className="w-12 h-12 mb-3 text-gray-400" />
                     <p className="text-gray-500 font-medium">Você ainda não despachou nenhum pedido.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                     {historico.map(p => (
                       <div key={p.id} onClick={() => setSelectedPedido(p)} className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 cursor-pointer border border-gray-200 dark:border-gray-700 hover:border-blue-400 transition-colors">
                          <div className="flex justify-between items-center">
                            <h4 className="font-bold text-gray-800 dark:text-gray-200">#{p.id}</h4>
                            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${p.status === 'entregue' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {p.tipo_pedido === 'mesa' ? 'Mesa Encerrada' : p.status}
                            </span>
                          </div>
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mt-2">
                             {p.tipo_pedido === 'mesa' ? `MESA ${p.mesa}` : '🛵 DELIVERY'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(p.created_at)}</p>
                          <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <span className="text-xs font-semibold text-gray-500">{p.metodo_pagamento_detalhado || p.metodo_pagamento}</span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white">R$ {Number(p.total).toFixed(2)}</span>
                          </div>
                       </div>
                     ))}
                  </div>
                )}
             </div>
          </div>
        )}
      </div>

      {renderPedidoModal()}

      {/* Modal Confirmar Limpeza Histórico */}
      {showClearHistoryModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setShowClearHistoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col items-center text-center ring-1 ring-red-500/50" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-black text-gray-900 dark:text-white mb-2">Expurgar Histórico?</h3>
            <p className="text-sm text-gray-500 font-medium mb-6">
              Todos os pedidos <strong className="text-gray-700 dark:text-gray-300">Entregues e Cancelados</strong> serão excluídos permanentemente. Essa ação é irreversível.
            </p>
            <input 
               type="password" 
               placeholder="Senha Administrativa" 
               className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl mb-4 text-center font-bold tracking-widest focus:ring-2 focus:ring-red-500 outline-none text-gray-900 dark:text-gray-100"
               value={adminPassword}
               onChange={e => setAdminPassword(e.target.value)}
               autoFocus
               onKeyDown={(e) => e.key === 'Enter' && handleClearHistory()}
            />
            <div className="flex gap-3 w-full">
              <button onClick={() => setShowClearHistoryModal(false)} className="flex-1 py-3 text-gray-600 font-bold bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition-colors">
                 Cancelar
              </button>
              <button onClick={handleClearHistory} className="flex-1 py-3 text-white font-bold bg-red-600 hover:bg-red-700 rounded-xl shadow-lg shadow-red-600/20 transition-colors">
                 Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PedidosPage;
